import type { ErrorEvent, StackFrame } from '@sentry/nuxt'

/**
 * Third-party noise filters for Sentry events, read back in the `beforeSend`
 * hook of `sentry.client.config.ts`.
 */

/**
 * The Meta apps (Instagram / Facebook) inject their own script into the in-app
 * webview, and it talks to the native bridge via `window.webkit.messageHandlers`
 * on `pagehide`. In some webview states that object is absent, so it throws —
 * as the user is already leaving the page, with no functional impact.
 *
 * The injected script runs in our page context, which is why Sentry attributes
 * the frames (`sendDataToNative`, `sendPageHideMessage` — neither exists in this
 * codebase) to our URL and maps them onto minified CSS.
 *
 * Matched on the message *and* the user agent rather than via `ignoreErrors`, so
 * a genuine native-bridge error from a real browser would still be reported. See
 * KFT-3905.
 */
const META_BRIDGE_MESSAGE = /window\.webkit\.messageHandlers/
const META_WEBVIEW_USER_AGENT = /Instagram|FBAV|FBAN/i

/**
 * True when the event is the Meta in-app webview native bridge error, which is
 * safe to drop. Both the message and the user agent must match: anything that
 * matches only one of them is reported as usual, so this can never swallow an
 * error of ours.
 */
export const isMetaWebviewBridgeNoise = (event: ErrorEvent): boolean => {
  const userAgent = event.request?.headers?.['User-Agent']

  if (!userAgent || !META_WEBVIEW_USER_AGENT.test(userAgent)) return false

  // Global `onerror` events carry a single exception, but check every value so a
  // linked cause still matches, and fall back to `message` for message events.
  return (
    (event.exception?.values ?? []).some(
      ({ value }) => !!value && META_BRIDGE_MESSAGE.test(value)
    ) ||
    (!!event.message && META_BRIDGE_MESSAGE.test(event.message))
  )
}

/**
 * `SyntaxError: Unexpected end of input` — the browser failing to parse a
 * truncated script (an aborted navigation, a flaky connection, or a
 * third-party/extension script injected and `eval`'d in our page context).
 *
 * We see three shapes of it in production, none originating in our bundle:
 *  - no stack at all (WEBSTORE-280)
 *  - frames inside the VWO A/B bootstrap, attributed to the page-document URL
 *    because that snippet is inlined in <head> (WEBSTORE-2E2, see useVwoScript)
 *  - a single `blob:` URL frame from a dynamically created script (WEBSTORE-2E0)
 *
 * A genuine `SyntaxError` from our own code (e.g. `JSON.parse` on a bad
 * response) always carries at least one frame in a real bundle asset — Nuxt
 * emits chunks under `/_nuxt/` as `.js`/`.mjs`. So we drop this error only when
 * *no* frame points at our own code, which keeps real bugs reported.
 */
const SYNTAX_ERROR = /SyntaxError/
const UNEXPECTED_END_OF_INPUT = /Unexpected end of input/

// A frame in our own bundle: a real `.js`/`.mjs` asset (Nuxt serves chunks from
// `/_nuxt/`). The noise frames never match — they are empty, a `blob:` URL,
// `<anonymous>`, the page-document URL, or VWO's `j.php`.
const FIRST_PARTY_SCRIPT = /\/_nuxt\/|\.m?js(?:[?#]|$)/

const isFirstPartyFrame = (frame: StackFrame): boolean =>
  [frame.filename, frame.abs_path, frame.module].some(
    (path) => !!path && FIRST_PARTY_SCRIPT.test(path)
  )

const isUnexpectedEndOfInputMessage = (text: string | undefined): boolean =>
  !!text && SYNTAX_ERROR.test(text) && UNEXPECTED_END_OF_INPUT.test(text)

/**
 * True when the event is an `Unexpected end of input` SyntaxError that carries
 * no frame from our own bundle — i.e. third-party/injected noise, safe to drop.
 * Anything with a first-party frame (a real bug of ours) is reported as usual.
 */
export const isUnexpectedEndOfInputNoise = (event: ErrorEvent): boolean => {
  const values = event.exception?.values ?? []

  // Message-only events (global onerror without an exception object).
  if (values.length === 0) return isUnexpectedEndOfInputMessage(event.message)

  // The type/value split varies (`SyntaxError` + `Unexpected end of input`, or
  // `Uncaught SyntaxError: Unexpected end of input` in `value`), so match the
  // two joined together.
  const matchesMessage = values.some(({ type, value }) =>
    isUnexpectedEndOfInputMessage(`${type ?? ''}: ${value ?? ''}`)
  )
  if (!matchesMessage) return false

  const frames = values.flatMap(({ stacktrace }) => stacktrace?.frames ?? [])
  return !frames.some(isFirstPartyFrame)
}
